import { useTranslation } from '@/app/i18n/client'
import React from 'react'

interface PaginationProps {
  currentPage: number
  pageCount: number
  visiblePages?: number
  onPageChange: (page: number) => void
}

const LimitPagination: React.FC<PaginationProps> = ({
  currentPage,
  pageCount,
  visiblePages = 5,
  onPageChange,
}) => {
  const { t } = useTranslation()
  const handlePreviousPage = () => {
    onPageChange(Math.max(currentPage - 1, 1))
  }
  const handleNextPage = () => {
    onPageChange(Math.min(currentPage + 1, pageCount))
  }
  const renderPaginationNumbers = () => {
    const pages: JSX.Element[] = []

    const addPage = (page: number) => {
      pages.push(
        <span
          key={`${page}_pagination`}
          className={`flex justify-center items-center rounded-full h-[24px] w-[24px] pt-[5px] pb-[4px] mx-2 text-[grey] font-ns_bold cursor-pointer ${
            currentPage === page ? '!bg-secondary !text-[white]' : ''
          }`}
          onClick={() => onPageChange(page)}
        >
          {page}
        </span>
      )
    }

    // Always show the first page
    addPage(1)

    // Show ellipsis if necessary before the current page
    if (currentPage > visiblePages && pageCount > visiblePages + 2) {
      pages.push(
        <span
          key="start-ellipsis"
          className="flex gap-[1px] mx-2 relative text-primary-foreground"
        >
          <span className="text-lg relative -top-1">.</span>
          <span className="text-lg relative -top-1">.</span>
          <span className="text-lg relative -top-1">.</span>
        </span>
      )
    }

    // Calculate range of pages to show around the current page
    let startPage = Math.max(currentPage - Math.floor(visiblePages / 2), 2)
    const endPage = Math.min(startPage + visiblePages - 1, pageCount - 1)

    // Ensure we don't exceed page boundaries
    if (endPage - startPage < visiblePages - 1) {
      startPage = Math.max(2, endPage - visiblePages + 2)
    }

    // Add pages to the list
    for (let i = startPage; i <= endPage; i++) {
      addPage(i)
    }

    // Show ellipsis if necessary after the current page
    if (endPage < pageCount - 1) {
      pages.push(
        <span
          key="end-ellipsis"
          className="flex gap-[1px] relative text-primary-foreground"
        >
          <span className="text-lg relative -top-1">.</span>
          <span className="text-lg relative -top-1">.</span>
          <span className="text-lg relative -top-1">.</span>
        </span>
      )
    }

    // Always show the last page
    if (pageCount > 1) {
      addPage(pageCount)
    }

    return pages
  }

  return (
    <div className="text-xs flex items-center my-[20px] mobile-width mobile:justify-center">
      <div className="text-[#656565]">
        <span
          className={`mx-[10px] ${currentPage === 1 ? 'cursor-not-allowed text-gray_text' : 'cursor-pointer'}`}
          onClick={handlePreviousPage}
        >
          {t('Previous')}
        </span>
      </div>
      <div className="flex items-center">{renderPaginationNumbers()}</div>
      <div className="text-[#656565]">
        <span
          className={`mx-[10px] mobile:mx-[5px] ${currentPage === pageCount ? 'cursor-not-allowed text-gray_text' : 'cursor-pointer'}`}
          onClick={handleNextPage}
        >
          {t('Next')}
        </span>
      </div>
    </div>
  )
}

export default LimitPagination
